// src/utils/logger.js

/**
 * Verbose logging is allowed ONLY on local, dev, and staging hosts.
 * Production must never log verbose output — so we fail CLOSED: any host
 * we can't positively identify as non-prod stays silent (this also covers
 * a missing or unparseable REACT_APP_BASE_URL).
 */
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '0.0.0.0'];
const NON_PROD_HOST_SUFFIXES = ['hipster-virtual.com']; // dev + staging live here

function resolveHostname() {
  const rawBaseUrl = process.env.REACT_APP_BASE_URL;
  if (!rawBaseUrl) return null;
  try {
    return new URL(rawBaseUrl).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function resolveLoggingEnabled(hostname) {
  if (!hostname) return false;
  if (LOCAL_HOSTS.includes(hostname)) return true;
  return NON_PROD_HOST_SUFFIXES.some(
    (suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`)
  );
}

const currentHostname = resolveHostname();
console.log(`Logger initialized. Debug logs: ${resolveLoggingEnabled(currentHostname)}`);
const isLoggingEnabled = resolveLoggingEnabled(currentHostname);

/**
 * No-op function (used in production for log/info/debug/warn)
 */
const noop = () => {};

/**
 * Logger object with methods that conditionally suppress logs
 */
const logger = {
  /**
   * log() — Shown in local/dev/staging, suppressed in production
   */
  log: isLoggingEnabled ? (...args) => {
    console.log('[APP]', ...args);
  } : noop,

  /**
   * info() — Shown in local/dev/staging, suppressed in production
   */
  info: isLoggingEnabled ? (...args) => {
    console.info('[INFO]', ...args);
  } : noop,

  /**
   * debug() — Shown in local/dev/staging, suppressed in production
   */
  debug: isLoggingEnabled ? (...args) => {
    console.debug('[DEBUG]', ...args);
  } : noop,

  /**
   * warn() — Shown in local/dev/staging, suppressed in production
   */
  warn: isLoggingEnabled ? (...args) => {
    console.warn('[WARN]', ...args);
  } : noop,

  /**
   * error() — ALWAYS shown, even in production
   */
  error: (...args) => {
    console.error('[ERROR]', ...args);
  },

  /**
   * Utility: Log the current environment (derived from REACT_APP_BASE_URL host)
   */
  logEnvironment: () => {
    // const host = currentHostname || 'unknown';
    logger.info(`Debug logs: ${isLoggingEnabled}`);
  },
};

export default logger;
export { isLoggingEnabled };